import { ReactElement, FC, ChangeEvent } from "react";
import { LooseObject } from "../interfaces/LooseObject";
import {
    Box,
    Button,
    Container,
    FormControl,
    TextField,
} from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import SnackBar from "./SnackBar";
import LoadingOverlay from "react-loading-overlay-ts";
import { AuthService } from "../services/auth.service";
import { SignUpService } from "../services/signup.service";
import { StringUtils } from "../utils/string.utils";

const Login: FC<LooseObject> = (props: any): ReactElement => {
    const [loading, setLoading] = React.useState(false);
    const [snackBarMessage, setSnackBarMessage] = React.useState("");
    const [snackBarOpen, setSnackBarOpen] = React.useState(false);
    const [snackBarType, setSnackBarType] = React.useState("success");
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [otp, setOTP] = React.useState("");
    const [isSent, setIsSent] = React.useState(false);
    const [errors, setErrors] = React.useState({ email: "", password: "", otp: "" });
    const authService = new AuthService();
    const SignupService = new SignUpService();

    const handleSnackBarClose = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSnackBarOpen(false);
    };

    // Returns the error message for a single field (empty string when valid).
    const getFieldError = (fieldName: string, value: string): string => {
        switch (fieldName) {
            case "email":
                if (!value) return "Email is required.";
                return StringUtils.isValidEmail(value) ? "" : "Please enter a valid email address.";
            case "password":
                return value ? "" : "Password is required.";
            case "otp":
                if (!isSent) return "";
                return value ? "" : "OTP is required.";
            default:
                return "";
        }
    };

    const validateField = (fieldName: string, value: string) => {
        setErrors((prevErrors) => ({
            ...prevErrors,
            [fieldName]: getFieldError(fieldName, value),
        }));
    };

    const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as string;
        setEmail(value);
        validateField("email", value);
    };

    const handlePasswordChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as string;
        setPassword(value);
        validateField("password", value);
    };

    const handleOtpChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value as string;
        setOTP(value);
        validateField("otp", value);
    };
    const handleKeyPress = (event: any) => {
        if (event.key === "Enter") {
            handleLogin();
        }
    };

    const handleLogin = () => {
        if (loading) return; // guard against double-submit while a request is in flight

        // Compute validity synchronously rather than reading the (still-stale,
        // pre-update) `errors` state right after calling validateField -- doing
        // that let a blank form pass validation on the very first click.
        const newErrors = {
            email: getFieldError("email", email),
            password: getFieldError("password", password),
            otp: getFieldError("otp", otp),
        };
        setErrors(newErrors);

        if (!Object.values(newErrors).every((error) => error === "")) {
            return;
        }

        setLoading(true);
        if (!isSent) {
            SignupService
                .sendEmailOtp({ email: email, password: password, isRegister:false })
                .then((result: any) => {
                    if (result) {
                        setIsSent(true);
                        setSnackBarMessage("Please enter the OTP sent to your email.");
                        setSnackBarType("success");
                        setSnackBarOpen(true);
                    } else {
                        setSnackBarMessage("Failed to send OTP. Please try again.");
                        setSnackBarType("error");
                        setSnackBarOpen(true);
                    }
                    setLoading(false);
                })
                .catch((err: any) => {
                    setLoading(false);
                    setSnackBarMessage(StringUtils.toErrorMessage(err));
                    setSnackBarType("error");
                    setSnackBarOpen(true);
                });
        } else {
            authService
                .login({ email, password, otp })
                .then((result: any) => {
                    setSnackBarMessage("Login Successfully");
                    setSnackBarType("success");
                    setSnackBarOpen(true);
                    setLoading(false);
                    props.action(true);
                })
                .catch((err: any) => {
                    setLoading(false);
                    setSnackBarMessage(StringUtils.toErrorMessage(err));
                    setSnackBarType("error");
                    setSnackBarOpen(true);
                    props.action(false);
                });
        }
    };

    const LogoProps: any = {
        height: "auto",
        width: "100%",
    };

    return (
        <React.Fragment>
            <Container
                sx={{
                    maxWidth: "100%",
                    width: "100%",
                    padding: "15px",
                    textAlign: "center",
                }}
            >
                <LoadingOverlay active={loading} spinner className="hsc-loader">
                    <Box
                        sx={{
                            padding: "25px 50px",
                            textAlign: "center",
                            background: "#f8f6f6",
                            width: "100%",
                            maxWidth: "500px",
                            margin: "0 auto",
                        }}
                        onKeyPress={handleKeyPress}
                    >
                        <SnackBar
                            id="snackbar-login"
                            message={snackBarMessage}
                            type={snackBarType}
                            open={snackBarOpen}
                            close={handleSnackBarClose}
                        />
                        <div>
                            <Logo {...LogoProps} />
                        </div>
                        <FormControl size="small" fullWidth sx={{ marginBlock: "5px" }}>
                            <TextField
                                sx={{ marginBlock: "10px" }}
                                required
                                id="outlined-email"
                                label="Email"
                                placeholder="Email"
                                size="small"
                                autoComplete="username"
                                disabled={isSent}
                                value={email}
                                onChange={handleEmailChange}
                                error={errors.email != ""}
                                helperText={errors.email}
                            />
                        </FormControl>
                        <FormControl size="small" fullWidth sx={{ marginBlock: "5px" }}>
                            <TextField
                                sx={{ marginBlock: "10px" }}
                                required
                                id="outlined-user-name"
                                label="Password"
                                placeholder="Password"
                                size="small"
                                type="password"
                                autoComplete="current-password"
                                disabled={isSent}
                                value={password}
                                onChange={handlePasswordChange}
                                error={errors.password != ""}
                                helperText={errors.password}
                            />
                        </FormControl>

                        <FormControl size="small" fullWidth sx={{ marginBlock: "5px", display: isSent ? '' : 'none' }}>
                            <TextField
                                sx={{ marginBlock: "10px" }}
                                required
                                id="outlined-otp"
                                label="OTP"
                                placeholder="OTP"
                                size="small"
                                name="otp"
                                disabled={!isSent}
                                className={props.shouldHide ? 'hidden' : undefined}
                                value={otp}
                                onChange={handleOtpChange}
                                error={errors.otp != ""}
                                helperText={errors.otp}
                            />
                        </FormControl>
                        <FormControl
                            size="small"
                            sx={{ marginBlock: "15px", padding: "25px" }}
                        >
                            <Button
                                id="login-submit"
                                variant="contained"
                                onClick={handleLogin}
                                disabled={loading}
                                size="large"
                                color="primary"
                                disableRipple={false}
                                sx={{ padding: "5px 18px", bgcolor: "#c72825" }}
                            >
                                Login
                            </Button>
                        </FormControl>
                        <Box sx={{ display: "flex", justifyContent: "center", gap: "16px", marginBottom: "16px" }}>
                            <Link to="/register">Create an account</Link>
                            <Link to="/reset-password">Forgot password?</Link>
                        </Box>
                    </Box>
                </LoadingOverlay>
            </Container>
        </React.Fragment>
    );
};

export default Login;
